import { useCallback, useEffect, useState } from "react";
import { usePreference } from "../lib/preferences";

/** Presenter themes, in the order D cycles through them. Each is a token block in styles/tokens.css. */
export const THEMES = [
  { id: "light", label: "Light", note: "Default" },
  { id: "dark", label: "Dark", note: "Dim rooms" },
  { id: "midnight", label: "Midnight", note: "Deep blue" },
  { id: "paper", label: "Warm paper", note: "Soft on the eyes" },
  { id: "contrast", label: "High contrast", note: "Washed-out projectors" },
] as const;

export type Theme = (typeof THEMES)[number]["id"];

const isTheme = (v: unknown): v is Theme => THEMES.some((t) => t.id === v);

/** ?theme=<id> wins (and is saved); otherwise the stored choice. */
function fromUrl(): Theme | null {
  const q = new URLSearchParams(window.location.search).get("theme");
  return isTheme(q) ? q : null;
}

/**
 * Presenter and notes window only: sets <html data-theme> while mounted, so other pages stay light.
 * Returns the theme, a setter, and `cycle` (D) for the next theme in THEMES.
 */
export function usePresenterTheme(): [Theme, (t: Theme) => void, () => void] {
  const [stored, save] = usePreference<Theme>("theme", "light");
  const [url] = useState(fromUrl);
  useEffect(() => {
    if (url) save(url);
  }, [url, save]);
  const theme: Theme = isTheme(stored) ? stored : "light";
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    return () => {
      delete root.dataset.theme;
    };
  }, [theme]);
  const cycle = useCallback(() => {
    const i = THEMES.findIndex((t) => t.id === theme);
    save(THEMES[(i + 1) % THEMES.length]!.id);
  }, [theme, save]);
  return [theme, save, cycle];
}
